import { useEffect, useRef, useState, useCallback } from 'react';
import socketServiceManager from 'app/socketService';
import { EmailTemplatesSocketService } from 'app/socketService/emailTemplates/emailTemplatesSocketService';

export interface SocketMessageItem {
  eventName: string;
  data: any;
  timestamp: number;
}

/**
 * Manages socket connection and dealer-based subscription for Email Templates page.
 * - Connects to global socket on mount.
 * - Subscribes to selected dealer when dealer is selected.
 * - On dealer change: unsubscribes from previous dealer, subscribes to new dealer.
 * - On unmount: unsubscribes from current dealer.
 * - Collects template_generation_* socket messages for UI; on template_generation_completed calls onTemplateUpdate and sets generationCompleted.
 */
export interface CompletionResult {
  generated?: number;
  skipped?: number;
  errors?: Array<{ message?: string } | string>;
  dealer_id?: string;
}

export function useEmailsGridSocket(
  selectedDealerId: string,
  onTemplateUpdate?: () => void,
  onGenerationCompleted?: () => void,
  onProgressUpdate?: (progress: { current: number; total: number }) => void,
  onSocketMessage?: (message: SocketMessageItem) => void,
  initialProgress?: { current: number; total: number },
  initialSocketMessages?: SocketMessageItem[],
): {
  socketMessages: SocketMessageItem[];
  generationCompleted: boolean;
  /** Payload from template_generation_completed – for showing in a standalone completion modal (no progress card dependency). */
  completionResult: CompletionResult | null;
  /** { current, total } from template_generation_progress; used to drive progress % (current/total*100). Starts at { current: 0, total: 1 } so progress shows 0. */
  progressFromSocket: { current: number; total: number };
  clearGenerationState: () => void;
} {
  const INACTIVITY_TIMEOUT_MS = 3 * 60 * 1000;

  const serviceRef = useRef<EmailTemplatesSocketService | null>(null);
  const previousDealerIdRef = useRef<string | null>(null);
  const onTemplateUpdateRef = useRef(onTemplateUpdate);
  const onGenerationCompletedRef = useRef(onGenerationCompleted);
  const onProgressUpdateRef = useRef(onProgressUpdate);
  const onSocketMessageRef = useRef(onSocketMessage);
  const activeDealerCallbackRef = useRef<
    ((eventName: string, data: any) => void) | null
  >(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMessageTimestampRef = useRef<number | null>(null);

  onTemplateUpdateRef.current = onTemplateUpdate;
  onGenerationCompletedRef.current = onGenerationCompleted;
  onProgressUpdateRef.current = onProgressUpdate;
  onSocketMessageRef.current = onSocketMessage;

  const [socketMessages, setSocketMessages] = useState<SocketMessageItem[]>(
    initialSocketMessages || []
  );
  const [generationCompleted, setGenerationCompleted] = useState(false);
  const [completionResult, setCompletionResult] = useState<CompletionResult | null>(null);
  const [progressFromSocket, setProgressFromSocket] = useState(
    initialProgress || { current: 0, total: 1 }
  );

  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const scheduleInactivityTimeout = (dealerId: string) => {
    if (!dealerId) return;

    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      const service = serviceRef.current;
      const activeDealerId = previousDealerIdRef.current;

      if (!service || !activeDealerId || activeDealerId !== dealerId) {
        return;
      }

      const lastTs = lastMessageTimestampRef.current;
      const now = Date.now();
      const elapsed = lastTs ? now - lastTs : INACTIVITY_TIMEOUT_MS;

      if (elapsed < INACTIVITY_TIMEOUT_MS) {
        return;
      }

      try {
        service.unsubscribeFromDealer(dealerId);
        if (previousDealerIdRef.current === dealerId) {
          previousDealerIdRef.current = null;
        }
      } catch (error) {
        console.error(
          '❌ [EmailsGrid] Failed to unsubscribe after inactivity timeout:',
          error,
        );
      } finally {
        clearInactivityTimer();
      }

      setGenerationCompleted(true);
      setCompletionResult(prev =>
        prev ?? {
          dealer_id: dealerId,
          errors: [
            {
              message:
                'Template generation timed out after 3 minutes without socket updates.',
            },
          ],
        },
      );

      onTemplateUpdateRef.current?.();
      onGenerationCompletedRef.current?.();
    }, INACTIVITY_TIMEOUT_MS);
  };

  const clearGenerationState = useCallback(() => {
    setSocketMessages([]);
    setGenerationCompleted(false);
    setCompletionResult(null);
    setProgressFromSocket({ current: 0, total: 1 });
    clearInactivityTimer();
  }, []);

  // Initialize socket service once on mount
  useEffect(() => {
    const socketUrl =
      process.env.REACT_APP_WS_URL ||
      process.env.REACT_APP_API_URL ||
      'http://localhost:3000';

    const socketConfig = {
      url: socketUrl,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    };

    try {
      socketServiceManager.initializeGlobalSocket(socketConfig);
      const service = EmailTemplatesSocketService.getInstance(socketConfig);
      socketServiceManager.registerService(service);
      serviceRef.current = service;
      service.connect();
    } catch (error) {
      console.error(
        '❌ [EmailsGrid] Failed to initialize email templates socket:',
        error,
      );
    }

    return () => {
      if (
        serviceRef.current &&
        previousDealerIdRef.current &&
        activeDealerCallbackRef.current
      ) {
        serviceRef.current.unsubscribeFromDealer(
          previousDealerIdRef.current,
          activeDealerCallbackRef.current,
        );
        previousDealerIdRef.current = null;
      }
      activeDealerCallbackRef.current = null;
      clearInactivityTimer();
    };
  }, []);

  // Subscribe to dealer when selected; unsubscribe from previous when dealer changes
  useEffect(() => {
    const service = serviceRef.current;
    if (!service) return;

    const previousDealerId = previousDealerIdRef.current;

    if (previousDealerId && previousDealerId !== selectedDealerId) {
      if (activeDealerCallbackRef.current) {
        service.unsubscribeFromDealer(
          previousDealerId,
          activeDealerCallbackRef.current,
        );
      }
      previousDealerIdRef.current = null;
      activeDealerCallbackRef.current = null;
      clearInactivityTimer();
    }

    if (!selectedDealerId) return;

    const callback = (eventName: string, data: any) => {
      if (eventName.startsWith('template_generation_')) {
        lastMessageTimestampRef.current = Date.now();
        scheduleInactivityTimeout(selectedDealerId);

        const newMessage: SocketMessageItem = { 
          eventName, 
          data, 
          timestamp: Date.now() 
        };
        
        setSocketMessages(prev =>
          [...prev, newMessage].slice(-100),
        );
        
        // Persist socket message to localStorage
        if (onSocketMessageRef.current) {
          onSocketMessageRef.current(newMessage);
        }
        
        if (eventName === 'template_generation_progress' && data?.message) {
          const total =
            typeof data.message.total === 'number' ? data.message.total : 1;
          const current =
            typeof data.message.current === 'number'
              ? Math.min(data.message.current, total)
              : 0;
          const newProgress = { current, total: Math.max(total, 1) };
          setProgressFromSocket(prev =>
            prev.total !== total || prev.current !== current
              ? newProgress
              : prev,
          );
          // Persist progress to Redux
          if (onProgressUpdateRef.current) {
            onProgressUpdateRef.current(newProgress);
          }
        }
        if (eventName === 'template_generation_completed') {
          const msg = data?.message;
          setCompletionResult(
            msg
              ? {
                  generated: msg.generated != null ? Number(msg.generated) : undefined,
                  skipped: msg.skipped != null ? Number(msg.skipped) : undefined,
                  errors: Array.isArray(msg.errors) ? msg.errors : undefined,
                  dealer_id: msg.dealer_id != null ? String(msg.dealer_id) : undefined,
                }
              : null,
          );
          setGenerationCompleted(true);
          onTemplateUpdateRef.current?.();
          onGenerationCompletedRef.current?.();
        }
      } else if (eventName.startsWith('email_template_')) {
        // Only template CRUD events should refresh grids — not nurturing automation
        // progress (nurturing_test_automation_*), which is handled elsewhere.
        onTemplateUpdateRef.current?.();
      }
    };

    service.subscribeToDealer(selectedDealerId, callback);
    previousDealerIdRef.current = selectedDealerId;
    activeDealerCallbackRef.current = callback;

    return () => {
      service.unsubscribeFromDealer(selectedDealerId, callback);
      if (previousDealerIdRef.current === selectedDealerId) {
        previousDealerIdRef.current = null;
      }
      if (activeDealerCallbackRef.current === callback) {
        activeDealerCallbackRef.current = null;
      }
    };
  }, [selectedDealerId]);

  return {
    socketMessages,
    generationCompleted,
    completionResult,
    progressFromSocket,
    clearGenerationState,
  };
}
